import { Link } from 'react-router';
import { styled } from '@mui/material/styles';

import CityPill from '../city/CityPill';
import { CITIES, switchCity, useCity } from '../city/useCity';
import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';

/**
 * The landing page at `/`, after Sam's mockup (`handoff/demo/landing-mockup.jpg`):
 * the lockup and the city pill, a three-line headline over the sky, and one
 * button into the app.
 *
 * Three layers. The photograph fills the page; the headline sits on it; and
 * over the headline, the same frame cut down to the four runners, laid with
 * the photograph's exact geometry so its pixels land on the photograph's own
 * runners. "Together." reads as behind them.
 *
 * Both images are sized by hand rather than with `object-fit`, so the ridge
 * can be placed: cover, then moved so the ridge sits at 62% of the page where
 * the crop allows it, and centred on the runners across. The headline and the
 * button are measured off the same ridge, so the composition holds from a
 * phone to a wide monitor.
 */

const BASE = import.meta.env.BASE_URL;

/** The photograph's proportions, and where its ridge and runners are, as fractions of it. */
const PHOTO_ASPECT = 2000 / 1245;
const RIDGE_IN_PHOTO = 0.6;
const RUNNERS_IN_PHOTO = 0.575;

const Stage = styled('main')({
  position: 'relative',
  width: '100%',
  height: '100dvh',
  minHeight: 520,
  overflow: 'hidden',
  background: '#8fa5ad',
  color: BRAND.parchment,
  // The layers below are measured in the stage's own units.
  containerType: 'size',
});

/**
 * One rule for both images, so the cut-out cannot drift off the photograph.
 * The custom properties are written here and resolved in each image, where
 * `cq` units mean the stage.
 */
const layer = {
  '--w': `max(100cqw, calc(100cqh * ${PHOTO_ASPECT}))`,
  '--h': `calc(var(--w) / ${PHOTO_ASPECT})`,
  position: 'absolute',
  width: 'var(--w)',
  height: 'var(--h)',
  maxWidth: 'none',
  // Ridge at 62% of the page, as far as the photograph reaches; never a gap.
  top: `clamp(calc(100cqh - var(--h)), calc(62cqh - ${RIDGE_IN_PHOTO} * var(--h)), 0px)`,
  // The runners centred across, when the photograph is wider than the page.
  left: `clamp(calc(100cqw - var(--w)), calc(50cqw - ${RUNNERS_IN_PHOTO} * var(--w)), 0px)`,
  userSelect: 'none',
  pointerEvents: 'none',
} as const;

const Photo = styled('img')({ ...layer, zIndex: 0 });
const People = styled('img')({ ...layer, zIndex: 2 });

/** Where the ridge lands on the page: the same geometry, in one expression. */
const RIDGE = `calc(clamp(calc(100cqh - var(--h)), calc(62cqh - ${RIDGE_IN_PHOTO} * var(--h)), 0px) + ${RIDGE_IN_PHOTO} * var(--h))`;
const ridgeVars = {
  '--w': `max(100cqw, calc(100cqh * ${PHOTO_ASPECT}))`,
  '--h': `calc(var(--w) / ${PHOTO_ASPECT})`,
} as const;

const Header = styled('header')(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 3,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
  padding: 'clamp(22px, 1.9cqw, 38px) clamp(22px, 1.9cqw, 40px)',
  [theme.breakpoints.down('sm')]: { padding: '18px 16px' },
}));

const Logo = styled('img')(({ theme }) => ({
  display: 'block',
  width: 'clamp(170px, 18cqw, 360px)',
  height: 'auto',
  [theme.breakpoints.down('sm')]: { width: 168 },
}));

/**
 * The headline, its foot on the ridge: "Together." stands on it, as in the
 * mockup, and the runners cross it.
 */
const Headline = styled('h1')(({ theme }) => ({
  ...ridgeVars,
  position: 'absolute',
  left: 16,
  right: 16,
  bottom: `calc(100cqh - ${RIDGE})`,
  zIndex: 1,
  margin: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  fontWeight: 'normal',
  // The Together line's descender hangs below the ridge, as the mockup's "g" does.
  transform: 'translateY(0.2em)',
  fontSize: 'clamp(96px, min(13cqw, 21cqh), 290px)',
  [theme.breakpoints.down('sm')]: { fontSize: 96 },
}));

const Lead = styled('span')(({ theme }) => ({
  fontFamily: '"Cy", "Cy Text", ui-sans-serif, system-ui, sans-serif',
  fontWeight: 700,
  fontSize: 'clamp(24px, min(3.05cqw, 4.9cqh), 50px)',
  lineHeight: 1.12,
  // Two lines, broken after "who", as in the mockup.
  maxWidth: '14.2em',
  textWrap: 'balance',
  marginBottom: '1.2em',
  [theme.breakpoints.down('sm')]: { fontSize: 22, maxWidth: '14em' },
}));

const Real = styled('span')(({ theme }) => ({
  fontFamily: FONTS.condensed,
  fontWeight: 500,
  // As wide as "Together." below it, as in the mockup.
  fontSize: '0.345em',
  lineHeight: 1,
  letterSpacing: '-0.01em',
  [theme.breakpoints.down('sm')]: { fontSize: 44 },
}));

const Together = styled('span')({
  fontFamily: FONTS.condensed,
  fontStyle: 'italic',
  fontWeight: 400,
  fontSize: '1em',
  lineHeight: 0.92,
  letterSpacing: '-0.015em',
  // Past the italic's overhang, so the full stop is not clipped.
  paddingRight: '0.06em',
});

const Explore = styled(Link)(({ theme }) => ({
  ...ridgeVars,
  position: 'absolute',
  zIndex: 3,
  top: `calc(${RIDGE} + clamp(64px, 12cqh, 150px))`,
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: 'clamp(54px, 4.5cqw, 90px)',
  padding: '0 1.4em',
  borderRadius: 999,
  background: BRAND.oliveLeaf,
  border: '1.5px solid rgba(243,238,231,0.75)',
  color: BRAND.parchment,
  fontFamily: FONTS.body,
  fontWeight: 400,
  fontSize: 'clamp(21px, 2.2cqw, 44px)',
  textDecoration: 'none',
  whiteSpace: 'nowrap',
  boxShadow: '0 6px 24px rgba(20, 24, 12, 0.25)',
  transition: 'filter 140ms ease',
  '&:hover': { filter: 'brightness(1.18)' },
  [theme.breakpoints.down('sm')]: {
    left: 16,
    right: 16,
    transform: 'none',
    height: 56,
    fontSize: 21,
  },
}));

export default function LandingPage() {
  const { city, chosen } = useCity();
  const short = (CITIES.find((c) => c.key === city) ?? CITIES[0]).short;

  return (
    <Stage>
      <Photo
        src={`${BASE}landing/hero.jpg`}
        srcSet={`${BASE}landing/hero-1000.jpg 1000w, ${BASE}landing/hero.jpg 2000w`}
        sizes={`max(100vw, ${Math.round(PHOTO_ASPECT * 100)}vh)`}
        alt=""
        fetchPriority="high"
      />

      <Header>
        <Logo src={`${BASE}commonground-logo.svg`} alt="CommonGround" width={422} height={68} />
        <CityPill size="landing" />
      </Header>

      <Headline>
        <Lead>Gatherings for people who want to make a difference.</Lead>
        <Real>Let’s do something real.</Real>
        <Together>Together.</Together>
      </Headline>

      <People src={`${BASE}landing/hero-people.webp`} alt="" aria-hidden />

      {/* Choosing to explore a city chooses it: the app's "Where are you?" is
          for a first visit that arrives by a link instead. */}
      <Explore to="/explore" onClick={() => !chosen && switchCity(city)}>
        Explore {short}
      </Explore>
    </Stage>
  );
}
